// =============================================================================
// SHANIZ GAMING ADMIN - FEATURE FLAGS
// =============================================================================

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { featureFlagService, Env, FeatureFlag, Region } from '../services/api';
import { usePermission } from '../hooks/usePermission';

const ENVS: Env[] = ['dev', 'staging', 'prod'];
const REGIONS: Region[] = ['KE', 'TZ', 'UG'];

// Flags are surfaced as isSystem=true by the backend when they're part of the
// seeded baseline set. We grey the delete button out for those.
type UiFlag = FeatureFlag & { isSystem?: boolean; category?: string };

const FeatureFlagsPage: React.FC = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const canEdit = usePermission('feature_flags.edit').allowed;

  const list = useQuery({ queryKey: ['feature-flags'], queryFn: featureFlagService.getAll });

  const flags = useMemo<UiFlag[]>(() => {
    // Backend emits `enabled`/`region`; this UI was written against `envs`/`regions`.
    // Normalise both shapes so toggles light up regardless of which the server sends.
    const raw = Array.isArray(list.data) ? list.data : [];
    const data: UiFlag[] = raw.map((f: any) => ({
      key: String(f?.key ?? ''),
      description: String(f?.description ?? ''),
      envs: (f?.envs && typeof f.envs === 'object' ? f.envs : f?.enabled && typeof f.enabled === 'object' ? f.enabled : {}) as FeatureFlag['envs'],
      regions: (f?.regions && typeof f.regions === 'object' ? f.regions : f?.region && typeof f.region === 'object' ? f.region : {}) as FeatureFlag['regions'],
      updatedAt: String(f?.updatedAt ?? ''),
      updatedBy: f?.updatedBy,
      isSystem: Boolean(f?.isSystem),
      category: f?.category ?? undefined,
    }));
    const q = search.trim().toLowerCase();
    return q ? data.filter((f) => f.key.toLowerCase().includes(q) || f.description?.toLowerCase().includes(q)) : data;
  }, [list.data, search]);

  const patchFlag = useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: Partial<FeatureFlag> }) => featureFlagService.update(key, patch),
    onMutate: async ({ key, patch }) => {
      await qc.cancelQueries({ queryKey: ['feature-flags'] });
      const prev = qc.getQueryData<FeatureFlag[]>(['feature-flags']);
      qc.setQueryData<FeatureFlag[]>(['feature-flags'], (old) =>
        (old || []).map((f) => (f.key === key ? { ...f, ...patch, envs: { ...f.envs, ...(patch.envs || {}) }, regions: { ...f.regions, ...(patch.regions || {}) } } : f))
      );
      return { prev };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['feature-flags'], ctx.prev);
      setSnackbar({ open: true, msg: e.message || 'Update failed', sev: 'error' });
    },
    onSuccess: () => setSnackbar({ open: true, msg: 'Flag updated', sev: 'success' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['feature-flags'] }),
  });

  const toggleEnv = (flag: UiFlag, env: Env) => {
    patchFlag.mutate({ key: flag.key, patch: { envs: { ...flag.envs, [env]: !flag.envs[env] } } });
  };
  const toggleRegion = (flag: UiFlag, region: Region) => {
    patchFlag.mutate({ key: flag.key, patch: { regions: { ...flag.regions, [region]: !flag.regions[region] } } });
  };

  // ------- Create-flag dialog state -------
  const [createOpen, setCreateOpen] = useState(false);
  const [newFlag, setNewFlag] = useState<{
    key: string;
    description: string;
    category: 'growth' | 'risk' | 'ops' | 'experiment';
    dev: boolean;
    staging: boolean;
    prod: boolean;
    KE: boolean;
    TZ: boolean;
    UG: boolean;
    enableImmediately: boolean;
    rolloutNote: string;
  }>({
    key: '',
    description: '',
    category: 'experiment',
    dev: true,
    staging: false,
    prod: false,
    KE: true,
    TZ: true,
    UG: true,
    enableImmediately: true,
    rolloutNote: '',
  });

  // Auto-sanitise key input into snake.case.dot.syntax as the user types.
  const sanitiseKey = (raw: string) =>
    raw
      .toLowerCase()
      .replace(/[^a-z0-9._]+/g, '.')
      .replace(/\.+/g, '.')
      .replace(/^\.+/, '');

  const createFlagMut = useMutation({
    mutationFn: (payload: Parameters<typeof featureFlagService.create>[0]) => featureFlagService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      setCreateOpen(false);
      setNewFlag({
        key: '',
        description: '',
        category: 'experiment',
        dev: true,
        staging: false,
        prod: false,
        KE: true,
        TZ: true,
        UG: true,
        enableImmediately: true,
        rolloutNote: '',
      });
      setSnackbar({ open: true, msg: 'Flag created', sev: 'success' });
    },
    onError: (e: any) =>
      setSnackbar({ open: true, msg: e?.response?.data?.message || e?.message || 'Create failed', sev: 'error' }),
  });

  const deleteFlagMut = useMutation({
    mutationFn: (key: string) => featureFlagService.remove(key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
      setSnackbar({ open: true, msg: 'Flag deleted', sev: 'success' });
    },
    onError: (e: any) =>
      setSnackbar({ open: true, msg: e?.response?.data?.message || e?.message || 'Delete failed', sev: 'error' }),
  });

  const newFlagKeySanitised = sanitiseKey(newFlag.key);
  const createValid = Boolean(newFlagKeySanitised) && newFlag.description.trim().length > 0;

  const handleCreateSubmit = () => {
    if (!createValid) return;
    createFlagMut.mutate({
      key: newFlagKeySanitised,
      description: newFlag.description.trim(),
      category: newFlag.category,
      enabled: newFlag.enableImmediately
        ? { dev: newFlag.dev, staging: newFlag.staging, prod: newFlag.prod }
        : { dev: false, staging: false, prod: false },
      regions: { KE: newFlag.KE, TZ: newFlag.TZ, UG: newFlag.UG },
    });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={700}>Feature Flags</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="Search flags"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ width: 280 }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => list.refetch()}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disabled={!canEdit}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            New flag
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          {list.isError && <Alert severity="error">Failed to load feature flags.</Alert>}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Description</TableCell>
                  {ENVS.map((e) => <TableCell key={e} align="center">{e}</TableCell>)}
                  {REGIONS.map((r) => <TableCell key={r} align="center">{r}</TableCell>)}
                  <TableCell>Updated</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.isLoading && Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                  </TableRow>
                ))}
                {!list.isLoading && flags.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography color="text.secondary" textAlign="center" py={3}>No flags match your search.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {flags.map((f) => (
                  <TableRow key={f.key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{f.key}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{f.description}</Typography>
                    </TableCell>
                    {ENVS.map((env) => (
                      <TableCell key={env} align="center">
                        <Tooltip title={canEdit ? '' : 'No permission'}>
                          <span>
                            <Switch
                              size="small"
                              disabled={!canEdit}
                              checked={Boolean(f.envs?.[env])}
                              onChange={() => toggleEnv(f, env)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    ))}
                    {REGIONS.map((rg) => (
                      <TableCell key={rg} align="center">
                        <Chip
                          size="small"
                          label={f.regions?.[rg] ? 'ON' : 'OFF'}
                          color={f.regions?.[rg] ? 'success' : 'default'}
                          onClick={canEdit ? () => toggleRegion(f, rg) : undefined}
                          clickable={canEdit}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {f.updatedAt ? dayjs(f.updatedAt).format('MMM D HH:mm') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={f.isSystem ? 'Seeded system flag — cannot be deleted' : (canEdit ? 'Delete flag' : 'No permission')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canEdit || f.isSystem || deleteFlagMut.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete feature flag "${f.key}"? This cannot be undone.`)) {
                                deleteFlagMut.mutate(f.key);
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ---------- Create Feature Flag Dialog ---------- */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={700}>New feature flag</Typography>
          <Typography variant="body2" color="text.secondary">
            Flags are immediately queryable from backend services. Choose your rollout carefully.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3.5} sx={{ mt: 1 }}>
            {/* Section 1 — Flag definition */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                1 · Flag definition
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth size="small"
                    label="Key"
                    placeholder="e.g. wallet.new_topup_flow"
                    value={newFlag.key}
                    onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value })}
                    required
                    helperText={
                      newFlag.key
                        ? `Will be saved as: ${newFlagKeySanitised || '(invalid)'}`
                        : 'Lowercase, dot-separated. We sanitise invalid characters automatically.'
                    }
                    error={newFlag.key.trim().length > 0 && !newFlagKeySanitised}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={newFlag.category}
                      label="Category"
                      onChange={(e) => setNewFlag({ ...newFlag, category: e.target.value as any })}
                    >
                      <MenuItem value="growth">Growth</MenuItem>
                      <MenuItem value="risk">Risk</MenuItem>
                      <MenuItem value="ops">Ops</MenuItem>
                      <MenuItem value="experiment">Experiment</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" multiline minRows={2}
                    label="Description"
                    placeholder="What does this flag control? Who reads it?"
                    value={newFlag.description}
                    onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                    required
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 2 — Rollout environments */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                2 · Rollout environments
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.dev} onChange={(e) => setNewFlag({ ...newFlag, dev: e.target.checked })} />}
                    label="dev"
                  />
                  <FormHelperText>Local dev + CI</FormHelperText>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.staging} onChange={(e) => setNewFlag({ ...newFlag, staging: e.target.checked })} />}
                    label="staging"
                  />
                  <FormHelperText>Shared staging env</FormHelperText>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.prod} onChange={(e) => setNewFlag({ ...newFlag, prod: e.target.checked })} />}
                    label="prod"
                  />
                  <FormHelperText>Live traffic — impacts real users</FormHelperText>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 3 — Region targeting */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                3 · Region targeting
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.KE} onChange={(e) => setNewFlag({ ...newFlag, KE: e.target.checked })} />}
                    label="Kenya (KE)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.TZ} onChange={(e) => setNewFlag({ ...newFlag, TZ: e.target.checked })} />}
                    label="Tanzania (TZ)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={<Switch checked={newFlag.UG} onChange={(e) => setNewFlag({ ...newFlag, UG: e.target.checked })} />}
                    label="Uganda (UG)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormHelperText>
                    Per-region switches gate the flag client-side. A region that is OFF will see the flag as disabled
                    even if the environment is on.
                  </FormHelperText>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Section 4 — Activation */}
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing={1}>
                4 · Activation
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newFlag.enableImmediately}
                        onChange={(e) => setNewFlag({ ...newFlag, enableImmediately: e.target.checked })}
                      />
                    }
                    label="Enable immediately"
                  />
                  <FormHelperText>
                    {newFlag.enableImmediately
                      ? 'Flag will go live with the env toggles above as soon as it is created.'
                      : 'Flag will be created OFF in all environments. Enable it later from this page.'}
                  </FormHelperText>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small" multiline minRows={2}
                    label="Rollout note (optional)"
                    placeholder="Link to runbook / JIRA / rollback plan"
                    value={newFlag.rolloutNote}
                    onChange={(e) => setNewFlag({ ...newFlag, rolloutNote: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Summary card */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={1} sx={{ display: 'block', mb: 1 }}>
                SUMMARY
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary">Key</Typography>
                  <Typography fontWeight={700} fontFamily="monospace">{newFlagKeySanitised || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography fontWeight={700}>{newFlag.category}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Environments</Typography>
                  <Typography fontWeight={700}>
                    {newFlag.enableImmediately
                      ? ENVS.filter((e) => (newFlag as any)[e]).join(', ') || 'none'
                      : 'none (manual activation)'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Regions</Typography>
                  <Typography fontWeight={700}>
                    {REGIONS.filter((r) => (newFlag as any)[r]).join(', ') || 'none'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCreateSubmit}
            disabled={!createValid || createFlagMut.isPending}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.primary', bgcolor: 'action.hover' },
              px: 3,
            }}
          >
            {createFlagMut.isPending ? 'Creating…' : 'Create flag'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeatureFlagsPage;
